import React, { FC, FormEvent, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { loginTemplate } from 'apollo/templates/super-admin';
import { SuperAdmin } from 'models/super-admin';

// Local dev only (DD-3715): mirrors the backend's SUPER_ADMIN_SKIP_TOTP, which is what actually skips
// the check. Setting this alone only relaxes the form; the backend still demands the code.
const SKIP_TOTP = process.env.REACT_APP_SUPER_ADMIN_SKIP_TOTP === 'true';

type Props = {
    onLoggedIn: (admin: SuperAdmin) => void;
};

const KNOWN_ERRORS = new Set(['INVALID_CREDENTIALS', 'ACCOUNT_LOCKED']);

/**
 * Super admin login: email, password and the authenticator app code in one
 * step, against super-admin-backend. Not the regular login page: super admins
 * have no account there.
 */
const Login: FC<Props> = ({ onLoggedIn }) => {
    const { t: translate } = useTranslation('super-admin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [errorKey, setErrorKey] = useState<string>();

    const [login, { loading }] = useMutation<{ login: SuperAdmin }>(loginTemplate, {
        onCompleted: ({ login: admin }) => onLoggedIn(admin),
        onError: (error) => {
            const code = error.graphQLErrors[0]?.extensions?.code as string | undefined;
            setErrorKey(code && KNOWN_ERRORS.has(code) ? code : 'LOGIN_FAILED');
            setTotpCode('');
        },
    });

    const submit = (event: FormEvent) => {
        event.preventDefault();
        setErrorKey(undefined);
        login({ variables: { email: email.trim(), password, totpCode: totpCode.trim() } });
    };

    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" px={2}>
            <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }}>
                <form onSubmit={submit} noValidate>
                    <Stack spacing={2}>
                        <Typography variant="h5">{translate('TITLE')}</Typography>
                        {errorKey ? <Alert severity="error">{translate(errorKey)}</Alert> : undefined}
                        <TextField
                            variant="outlined"
                            label={translate('EMAIL')}
                            type="email"
                            autoComplete="username"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            required
                            autoFocus
                            data-test="login.email"
                        />
                        <TextField
                            variant="outlined"
                            label={translate('PASSWORD')}
                            type="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            required
                            data-test="login.password"
                        />
                        <TextField
                            variant="outlined"
                            label={translate('TOTP_CODE')}
                            autoComplete="one-time-code"
                            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                            value={totpCode}
                            onChange={(event) => setTotpCode(event.target.value)}
                            required={!SKIP_TOTP}
                            helperText={SKIP_TOTP ? translate('TOTP_CODE_SKIPPED_LOCALLY') : undefined}
                            data-test="login.totp"
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading || !email || !password || (!SKIP_TOTP && totpCode.trim().length < 6)}
                            data-test="login.submit"
                        >
                            {translate('LOG_IN')}
                        </Button>
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default Login;
